import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { ROUTES } from '@/config/constants';
import { signInWithGoogle } from '../api/googleAuth';
import { authCallbackURL } from '../api/redirect';

/**
 * Auth modal — ported 1:1 from the OG WINR (index.html #authOverlay +
 * js/_v15_auth_real.js + handleDiscordLogin/handleGoogleLogin). Same markup,
 * copy, provider order, and icons; styling is Tailwind utilities.
 */

const STRENGTH_LABELS = ['Too weak', 'Weak', 'OK', 'Strong', 'Excellent'];

/** Password-strength fill: width + color per score 0–4 (OG .auth-strength-fill). */
const STRENGTH_FILL = [
  'w-0 bg-[#ff5b67]',
  'w-1/4 bg-[#ff5b67]',
  'w-1/2 bg-[#fbbf24]',
  'w-3/4 bg-[#60a5fa]',
  'w-full bg-[#39ff14]',
];

const FIELD_LABEL = 'flex items-center justify-between text-xs font-bold uppercase tracking-[0.3px] text-white/70';
const AUTH_INPUT =
  'w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3.5 text-sm text-white outline-none transition-all duration-200 placeholder:text-lo focus:border-white focus:shadow-[0_0_0_3px_rgba(255,255,255,0.2)]';

/** Password score 0–4. Mirrors OG `passwordScore` (_v15_auth_real.js:24). */
function passwordScore(pw: string): number {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(4, score);
}

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim());

type Mode = 'password' | 'magic';
type Notice = { kind: 'ok' | 'show'; text: string } | null;

interface SignInPanelProps {
  /** When rendered inside the auth overlay, closing dismisses the modal. As a
   *  standalone /sign-in page (no handler) the close button returns home. */
  onClose?: () => void;
}

export function SignInPanel({ onClose }: SignInPanelProps = {}) {
  const navigate = useNavigate();
  const close = onClose ?? (() => navigate(ROUTES.home));
  const [mode, setMode] = useState<Mode>('password');
  const [signUp, setSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [terms, setTerms] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const score = useMemo(() => passwordScore(password), [password]);
  const magic = mode === 'magic';

  const formValid = useMemo(() => {
    if (!isValidEmail(email)) return false;
    if (magic) return true;
    if (!password || password.length < 8) return false;
    if (signUp) {
      if (score < 2) return false;
      if (confirm !== password) return false;
      if (!terms) return false;
    }
    return true;
  }, [email, password, confirm, terms, signUp, magic, score]);

  function handleDiscordLogin() {
    void supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: { redirectTo: authCallbackURL(), scopes: 'identify email' },
    });
  }

  async function handleGoogleLogin() {
    try {
      setNotice(null);
      await signInWithGoogle();
      navigate(ROUTES.explore);
    } catch (err) {
      setNotice({ kind: 'show', text: 'Google login failed: ' + (err as Error).message });
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setNotice(null);
    if (!formValid) return;
    setLoading(true);

    if (magic) {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: authCallbackURL() },
      });
      setLoading(false);
      setNotice(
        error
          ? { kind: 'show', text: error.message }
          : { kind: 'ok', text: `Magic link sent. Check ${email} to finish signing in.` },
      );
      return;
    }

    const { data, error } = signUp
      ? await supabase.auth.signUp({ email, password, options: { emailRedirectTo: authCallbackURL() } })
      : await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);

    if (error) {
      setNotice({ kind: 'show', text: error.message });
      return;
    }
    if (signUp && !data.session) {
      setNotice({
        kind: 'ok',
        text: 'Check your inbox to confirm your email and finish setting up your account.',
      });
    }
    // On success the AuthProvider's onAuthStateChange takes over.
  }

  async function onForgotPassword() {
    if (!isValidEmail(email)) {
      setNotice({ kind: 'show', text: 'Enter your email above first.' });
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: authCallbackURL(),
    });
    setNotice(
      error
        ? { kind: 'show', text: error.message }
        : { kind: 'ok', text: `Password reset link sent to ${email}.` },
    );
  }

  const submitLabel = magic ? 'Email me a magic link' : signUp ? 'Create account' : 'Log in';

  return (
    <div className="relative w-full max-w-[560px] animate-auth-modal-in rounded-3xl border border-white/[0.08] bg-[rgba(10,10,12,0.85)] px-8 py-10 shadow-[0_24px_48px_rgba(0,0,0,0.6),inset_0_1px_1px_rgba(255,255,255,0.05)]">
      <button
        className="absolute right-5 top-5 z-10 flex cursor-pointer items-center justify-center rounded-full border-none bg-none p-2 text-lo transition-all duration-200 hover:bg-white/10 hover:text-white"
        onClick={close}
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className="mb-8 flex flex-col items-center text-center">
        {/* Big hollow outlined WINR wordmark (OG .auth-winnr-splash). */}
        <div className="pointer-events-none mx-auto mb-6 select-none text-[96px] font-black leading-none tracking-[-6px] [font-family:'Arial_Black',Impact,sans-serif] [-webkit-text-fill-color:transparent] [-webkit-text-stroke:2px_rgba(255,255,255,0.9)]">
          WINR
        </div>
        <h2 className="mb-2 text-2xl font-extrabold tracking-[-0.5px] text-white">
          {signUp ? 'Create your account' : 'Welcome back'}
        </h2>
        <p className="text-sm text-lo">{signUp ? 'Create your WINR account.' : 'Log in to your WINR account.'}</p>
      </div>

      <div className="flex flex-col gap-3">
        <button
          className="group relative flex w-full cursor-pointer items-center justify-center gap-3 overflow-hidden rounded-xl border border-[rgba(88,101,242,0.35)] bg-[linear-gradient(135deg,rgba(88,101,242,0.22)_0%,rgba(88,101,242,0.1)_100%)] p-3.5 text-sm font-semibold text-white shadow-[0_4px_20px_-8px_rgba(88,101,242,0.55),inset_0_1px_0_rgba(255,255,255,0.08)] transition-all duration-200 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] before:pointer-events-none before:absolute before:inset-0 before:bg-[linear-gradient(120deg,transparent_30%,rgba(255,255,255,0.18)_50%,transparent_70%)] before:[transform:translateX(-110%)] before:transition-transform before:duration-700 before:[transition-timing-function:cubic-bezier(0.16,1,0.3,1)] before:content-[''] hover:-translate-y-px hover:border-[rgba(88,101,242,0.6)] hover:bg-[linear-gradient(135deg,rgba(88,101,242,0.34)_0%,rgba(88,101,242,0.18)_100%)] hover:shadow-[0_8px_32px_-8px_rgba(88,101,242,0.7),inset_0_1px_0_rgba(255,255,255,0.12)] active:translate-y-px group-hover:before:[transform:translateX(110%)]"
          onClick={handleDiscordLogin}
        >
          <svg className="text-white [filter:drop-shadow(0_0_6px_rgba(88,101,242,0.6))]" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
          </svg>
          Continue with Discord
        </button>
        <button
          className="flex w-full cursor-pointer items-center justify-center gap-3 rounded-xl border border-white/[0.08] bg-white/[0.03] p-3.5 text-sm font-semibold text-hi transition-all duration-200 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-px hover:border-white/20 hover:bg-white/[0.08] active:translate-y-px"
          onClick={handleGoogleLogin}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          Continue with Google
        </button>
      </div>

      <div className="my-6 flex items-center text-center text-xs uppercase tracking-[1px] text-lo before:flex-1 before:border-b before:border-white/[0.08] before:content-[''] after:flex-1 after:border-b after:border-white/[0.08] after:content-['']">
        <span className="px-3">or</span>
      </div>

      <form className="flex flex-col gap-3.5" onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-1.5">
          <label className={FIELD_LABEL} htmlFor="authEmail">Email</label>
          <input
            id="authEmail"
            type="email"
            className={AUTH_INPUT}
            autoComplete="email"
            placeholder="you@domain.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        {!magic && (
          <div className="flex flex-col gap-1.5">
            <label className={FIELD_LABEL} htmlFor="authPassword">
              <span>Password</span>
              {!signUp && (
                <button
                  type="button"
                  className="cursor-pointer border-none bg-none text-[11px] font-semibold normal-case tracking-normal text-white no-underline"
                  onClick={onForgotPassword}
                >
                  Forgot?
                </button>
              )}
            </label>
            <div className="relative flex items-center">
              <input
                id="authPassword"
                type={showPw ? 'text' : 'password'}
                className={cn(AUTH_INPUT, 'flex-1 pr-11')}
                autoComplete={signUp ? 'new-password' : 'current-password'}
                placeholder="Your password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent text-white/50 transition-colors duration-200 hover:bg-white/[0.06] hover:text-white"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>

            {signUp && (
              <div className="mt-1">
                <div className="h-1 overflow-hidden rounded-[2px] bg-white/[0.08]">
                  <div className={cn('h-full rounded-[2px] transition-[width,background] duration-200', STRENGTH_FILL[score])} />
                </div>
                <div className="mt-1 text-[11px] text-white/50">
                  {password
                    ? STRENGTH_LABELS[score] + (score < 3 ? ' — add a number, symbol, and 8+ chars' : '')
                    : 'Use 8+ chars with a number and a symbol'}
                </div>
              </div>
            )}
          </div>
        )}

        {!magic && signUp && (
          <div className="flex flex-col gap-1.5">
            <label className={FIELD_LABEL} htmlFor="authConfirm">Confirm password</label>
            <input
              id="authConfirm"
              type="password"
              className={AUTH_INPUT}
              autoComplete="new-password"
              placeholder="Re-enter password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
        )}

        {!magic && signUp && (
          <label className="flex cursor-pointer select-none items-start gap-2.5 text-xs leading-[1.45] text-white/70">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 flex-none cursor-pointer accent-white"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
            />
            <span className="[&_a]:text-white [&_a]:underline [&_a]:decoration-white/30 [&_a:hover]:decoration-white">
              I agree to the <a href="#terms" onClick={(e) => e.preventDefault()}>Terms of Service</a> and{' '}
              <a href="#privacy" onClick={(e) => e.preventDefault()}>Privacy Policy</a>.
            </span>
          </label>
        )}

        {notice && (
          <div
            className={cn(
              'rounded-[10px] border px-3 py-2 text-xs',
              notice.kind === 'ok'
                ? 'border-[rgba(57,255,20,0.25)] bg-[rgba(57,255,20,0.08)] text-[#39ff14]'
                : 'border-[rgba(255,91,103,0.25)] bg-[rgba(255,91,103,0.08)] text-[#ff5b67]',
            )}
          >
            {notice.text}
          </div>
        )}

        <button
          type="submit"
          className={cn(
            'w-full rounded-xl border-none bg-white p-3.5 text-sm font-bold text-black transition-all duration-200 hover:-translate-y-px hover:bg-[#f0f0f0] active:translate-y-px',
            loading
              ? 'cursor-progress opacity-70 hover:translate-y-0 hover:bg-white'
              : !formValid && 'cursor-not-allowed opacity-40 hover:translate-y-0 hover:bg-white',
          )}
          disabled={loading || !formValid}
        >
          {submitLabel}
        </button>

        <button
          type="button"
          className="w-full cursor-pointer border-none bg-transparent p-1 text-xs font-semibold text-white/55 transition-colors duration-200 hover:text-white"
          onClick={() => {
            setMode((m) => (m === 'magic' ? 'password' : 'magic'));
            setNotice(null);
          }}
        >
          {magic ? 'Use password instead' : 'Use a magic link instead'}
        </button>
      </form>

      <div className="mt-6 text-center text-[13px] text-mid">
        <span>{signUp ? 'Already have an account?' : "Don't have an account?"}</span>
        <button
          type="button"
          className="ml-1 cursor-pointer border-none bg-none font-semibold text-white transition-colors duration-200 hover:text-accent"
          onClick={() => {
            setSignUp((s) => !s);
            setMode('password');
            setNotice(null);
          }}
        >
          {signUp ? 'Log in' : 'Sign up'}
        </button>
      </div>
    </div>
  );
}
